import { purchaseLineTotals } from './purchaseLines';
import PermissionButton, { currentCan } from '../../permissions/PermissionButton';
import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { 
  DataGrid, 
  gridClasses, 
  useGridApiRef,
  gridPageCountSelector,
  gridPageSelector,
  useGridApiContext,
  useGridSelector,
  gridExpandedSortedRowIdsSelector
} from '@mui/x-data-grid';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  styled,
  Pagination,
  PaginationItem,
  IconButton,
  Tooltip,
  CircularProgress,
  Typography,
  Stack,
  Chip
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Popover } from 'antd';
import { getArticleById } from '../../models/articleAPI';
import { formatAmount } from '../../utils/formatAmount';

// Custom pagination component with black theme
function CustomPagination() {
  const apiRef = useGridApiContext();
  const page = useGridSelector(apiRef, gridPageSelector);
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);

  return (
    <Pagination
      color="primary"
      variant="outlined"
      shape="rounded"
      size="small"
      page={page + 1}
      count={pageCount}
      renderItem={(props) => <PaginationItem {...props} disableRipple />}
      onChange={(event, value) => apiRef.current.setPage(value - 1)}
      sx={{ 
        marginTop: '5px',
        display: 'flex',
        justifyContent: 'center',
        '& .MuiPaginationItem-root': {
          minWidth: '24px',
          height: '24px',
          fontSize: '0.75rem',
          color: '#000',
          borderColor: '#000',
          '&.Mui-selected': {
            backgroundColor: '#000',
            color: '#fff',
            '&:hover': {
              backgroundColor: '#333',
            }
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          }
        }
      }}
    />
  );
}

// Page size constant
const PAGE_SIZE = 100;

const STOCK_COLOR_PALETTE = [
  '#1976d2',
  '#2e7d32',
  '#ed6c02',
  '#9c27b0',
  '#00838f',
  '#5e35b1',
  '#546e7a'
];

/**
 * NestedAchatTable - Reusable MUI DataGrid for Achat modules
 * Props:
 * - items: array of items to display
 * - handleSave: function to handle row updates
 * - handleDelete: function to handle row deletion
 * - validated: boolean indicating if the document is validated
 * - userPermissions: object with user permissions
 * - type: string - 'commande', 'reception', 'facture', 'avoir', 'demarque', 'correctionstock', or 'inventaire'
 * - showStock: boolean - whether to show the stock column (default: true)
 * - codeEnt: string - selected entrepot code to filter stock display
 * - quantitySign: number - display multiplier for quantities (default: 1)
 */
const NestedAchatTable = ({ 
  items = [], 
  handleSave, 
  handleDelete, 
  validated = false,
  userPermissions = { isManager: false },
  type = 'commande', // commande, reception, facture, avoir, demarque, correctionstock, inventaire
  onSelectionChange, // Callback for when selection changes
  selectedRows = [], // Controlled selection from parent
  selectionMode = false, // Whether to show checkboxes for selection
  showStock = true, // Whether to show the stock column
  codeEnt = null, // Selected entrepot code to filter stock display
  quantitySign = 1
}) => {
  const [confirmDelete, setConfirmDelete] = useState(null);
  const apiRef = useGridApiRef();
  const [focusedRowId, setFocusedRowId] = useState(null);
  const [focusedField, setFocusedField] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: PAGE_SIZE,
    page: 0,
  });
  
  // Add state to track temporary input values
  const [tempInputValues, setTempInputValues] = useState({});
  
  // Selection model state
  const [rowSelectionModel, setRowSelectionModel] = useState([]);

  // Info popover state
  const [infoRow, setInfoRow] = useState(null);
  const [articleCache, setArticleCache] = useState({});
  const [infoLoading, setInfoLoading] = useState(false);
  const [showInfoPopover, setShowInfoPopover] = useState(false);
  const [manualPopoverToggle, setManualPopoverToggle] = useState({});

  // Fetch article info helper
  const fetchArticleInfo = async (code) => {
    if (!code || articleCache[code]) return;
    try {
      setInfoLoading(true);
      const res = await getArticleById(code);
      const data = res?.data || res;
      setArticleCache(prev => ({ ...prev, [code]: data }));
    } catch (e) {
      console.error('Failed to fetch article info', e);
    } finally {
      setInfoLoading(false);
    }
  };

  // Process data directly with useMemo.
  // Purchase rows carry stable line IDs. Keep legacy fallbacks for other modules.
  const processedRows = useMemo(() => {
    if (!Array.isArray(items)) return [];
    const seenCodes = new Set();
    return items
      .filter(item => item && item.CODE_ART)
      .map((item, index) => {
        let rowId = item.id;
        if (rowId === undefined || rowId === null || rowId === '') {
          rowId = seenCodes.has(item.CODE_ART) ? `${item.CODE_ART}__${index}` : item.CODE_ART;
        }
        seenCodes.add(item.CODE_ART);
        return rowId === item.id ? item : { ...item, id: rowId };
      });
  }, [items]);
  
  // Debug: Track codeEnt changes
  useEffect(() => {
    console.log('[DEBUG] codeEnt prop changed:', {
      codeEnt,
      type: typeof codeEnt,
      isNull: codeEnt === null,
      isUndefined: codeEnt === undefined,
      value: codeEnt
    });
  }, [codeEnt]);
  
  // More reliable focus tracking using DOM observation
  const gridRef = useRef(null);
  
  // Set up a mutation observer to track focus changes in the grid
  useEffect(() => {
    if (!gridRef.current) return;
    
    const handleFocusChange = (mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'attributes' && 
            mutation.attributeName === 'tabindex' && 
            document.activeElement && 
            document.activeElement.closest('.MuiDataGrid-row')) {
          
          const rowElement = document.activeElement.closest('.MuiDataGrid-row');
          if (rowElement && rowElement.dataset.id) {
            setFocusedRowId(rowElement.dataset.id);
          }
        }
      }
    };
    
    const observer = new MutationObserver(handleFocusChange);
    observer.observe(gridRef.current, { 
      attributes: true, 
      subtree: true, 
      attributeFilter: ['tabindex'] 
    });
    
    setTimeout(() => {
      const focusedElement = document.querySelector('.MuiDataGrid-cell:focus');
      if (focusedElement) {
        const rowElement = focusedElement.closest('.MuiDataGrid-row');
        if (rowElement && rowElement.dataset.id) {
          setFocusedRowId(rowElement.dataset.id);
        }
      }
    }, 100);
    
    return () => {
      observer.disconnect();
    };
  }, []);
  
  // Additional keyboard event listener for more reliable focus tracking
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
        setTimeout(() => {
          const focusedElement = document.querySelector('.MuiDataGrid-cell:focus');
          if (focusedElement) {
            const rowElement = focusedElement.closest('.MuiDataGrid-row');
            if (rowElement && rowElement.dataset.id) {
              setFocusedRowId(rowElement.dataset.id);
            }
          }
        }, 0);
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);
  
  // Determine if a cell is editable
  const isCellEditable = (params) => {
    // If selection mode is active, disable all editing
    if (selectionMode) {
      return false;
    }

    // Inventory keeps its stock and tax reference fields read-only.
    if (type === 'inventaire') {
      return !validated && ['QTE', 'PA_HT', 'PA_TTC'].includes(params.field);
    }
    
    // Managers can edit everything
    if (userPermissions?.isManager) {
      return true;
    }
    
    // If validated, nothing is editable (except for managers)
    if (validated) {
      return false;
    }
    
    // Otherwise, editable cells depend on the document type.
    const editableFields = ['QTE'];
    if (['demarque', 'correctionstock'].includes(type)) {
      editableFields.push('REMARQUE');
    } else if (type !== 'transfert') {
      editableFields.push('PA_HT', 'PA_TTC', 'REMISE', 'TVA');
      if (type === 'avoir') editableFields.push('MAJ_STK');
    }
    
    return editableFields.includes(params.field);
  };
  
  // Helper: recalc HT/TTC based on TVA when one changes
  const recalcPrices = (row, oldRowRef) => {
    const tva = Number((row.TVA ?? oldRowRef?.TVA ?? 0));
    const changedField = Object.keys(row).find(k => oldRowRef && row[k] !== oldRowRef[k]);
    const updated = { ...row };
    if (changedField === 'PA_HT') {
      // Don't round PA_TTC, keep actual calculated value
      updated.PA_TTC = Number(updated.PA_HT) * (1 + tva / 100);
    } else if (changedField === 'PA_TTC') {
      // Don't round PA_HT, keep actual calculated value
      updated.PA_HT = tva ? Number(updated.PA_TTC) / (1 + tva / 100) : Number(updated.PA_TTC);
    } else if (changedField === 'TVA') {
      if (updated.PA_HT !== undefined && updated.PA_HT !== null) {
        // Don't round PA_TTC, keep actual calculated value
        updated.PA_TTC = Number(updated.PA_HT) * (1 + tva / 100);
      }
    }
    return updated;
  };

  // Process row update
  const processRowUpdate = (newRow, oldRow) => {
    try {
      const numericFields = ['QTE', 'PA_HT', 'PA_TTC', 'REMISE', 'TVA'];
      
      const processedRow = { ...newRow };
      
      numericFields.forEach(field => {
        if (field in processedRow && processedRow[field] !== -1) {
          if (processedRow[field] === '' || processedRow[field] === null) {
            processedRow[field] = 0;
          } else {
            const numValue = Number(processedRow[field].toString().replace(',', '.'));
            processedRow[field] = isNaN(numValue) ? 0 : numValue;
          }
        }
      });
      
      // Validation for REMISE (0-100)
      if ('REMISE' in processedRow) {
        processedRow.REMISE = Math.min(100, Math.max(0, processedRow.REMISE));
      }
      
      // Auto-calc HT/TTC based on TVA
      const prices = recalcPrices(processedRow, oldRow);
      const recalculatedRow = ['commande', 'reception', 'avoir', 'facture'].includes(type)
        ? purchaseLineTotals(prices) : prices;

      // Clear temporary input value
      setTempInputValues(prev => {
        const newValues = { ...prev };
        delete newValues[`${newRow.id}-${Object.keys(newRow).find(key => newRow[key] !== oldRow[key])}`];
        return newValues;
      });
      
      // Notify parent handler
      handleSave(recalculatedRow);
      return recalculatedRow;
    } catch (error) {
      console.error('Error in processRowUpdate:', error);
      return oldRow;
    }
  };

  // Add handler for cell value changes
  const handleCellValueChange = (params) => {
    const { id, field, value } = params;
    setTempInputValues(prev => ({
      ...prev,
      [`${id}-${field}`]: value
    }));
  };

  // Arrow navigation across pages: ArrowDown on the last row of a page moves to
  // the next page (focusing its first row), ArrowUp on the first row goes back.
  const handleCellKeyDown = useCallback((params, event) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    if (!apiRef.current) return;

    const sortedIds = gridExpandedSortedRowIdsSelector(apiRef);
    const { page, pageSize } = paginationModel;
    const indexInAll = sortedIds.indexOf(params.id);
    if (indexInAll === -1) return;

    const firstIndexOnPage = page * pageSize;
    const lastIndexOnPage = Math.min((page + 1) * pageSize - 1, sortedIds.length - 1);

    const goToRow = (targetIndex, newPage) => {
      event.preventDefault();
      event.defaultMuiPrevented = true;
      const targetId = sortedIds[targetIndex];
      setPaginationModel(prev => ({ ...prev, page: newPage }));
      setTimeout(() => {
        try {
          apiRef.current.setCellFocus(targetId, params.field);
          const rowIndex = apiRef.current.getRowIndexRelativeToVisibleRows?.(targetId);
          if (rowIndex !== undefined && rowIndex !== null && rowIndex >= 0) {
            apiRef.current.scrollToIndexes({ rowIndex });
          }
          setFocusedRowId(targetId);
        } catch (e) {
          // Row not rendered yet; focus will land on next interaction
        }
      }, 0);
    };

    if (event.key === 'ArrowDown' && indexInAll === lastIndexOnPage && indexInAll < sortedIds.length - 1) {
      goToRow(indexInAll + 1, page + 1);
    } else if (event.key === 'ArrowUp' && indexInAll === firstIndexOnPage && page > 0) {
      goToRow(indexInAll - 1, page - 1);
    }
  }, [apiRef, paginationModel]);
  
  const stockNumberFormatter = useMemo(
    () => new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }),
    []
  );

  const formatStockValue = useCallback(
    (value) => {
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) {
        return stockNumberFormatter.format(0);
      }
      return stockNumberFormatter.format(Math.max(0, numericValue));
    },
    [stockNumberFormatter]
  );

  const getWarehouseStocks = useCallback((row) => {
    console.log('[DEBUG] getWarehouseStocks called for row:', {
      id: row?.id,
      CODE_ART: row?.CODE_ART,
      hasArticle: !!row?.article,
      articleStocks: row?.article?.stocks,
      rowStocks: row?.stocks,
      CODE_ENT: row?.CODE_ENT,
      code_ent: row?.code_ent,
      qte_stk: row?.qte_stk,
      QTE_STK: row?.QTE_STK,
      articleQteStk: row?.article?.qte_stk
    });
    
    if (!row) {
      console.log('[DEBUG] getWarehouseStocks: row is null/undefined');
      return [];
    }
    
    // Check multiple possible locations for stocks data
    let stocks = row.article?.stocks || row.stocks;
    console.log('[DEBUG] getWarehouseStocks: stocks found:', {
      stocks,
      isArray: Array.isArray(stocks),
      length: Array.isArray(stocks) ? stocks.length : 'N/A',
      rawStocks: Array.isArray(stocks) ? JSON.stringify(stocks, null, 2) : 'Not an array'
    });
    
    if (!Array.isArray(stocks)) {
      // If no stocks array, try to build from row's direct stock info
      // Check if row has CODE_ENT/code_ent and qte_stk directly
      const rowCodeEnt = row.CODE_ENT || row.code_ent;
      const rowQteStk = row.qte_stk ?? row.QTE_STK ?? row.article?.qte_stk;
      
      console.log('[DEBUG] getWarehouseStocks: No stocks array, checking direct properties:', {
        rowCodeEnt,
        rowQteStk
      });
      
      if (rowCodeEnt && rowQteStk !== undefined) {
        const result = [{
          code_ent: rowCodeEnt,
          code: rowCodeEnt,
          qte_stk: rowQteStk,
          quantity: Number.isFinite(Number(rowQteStk)) ? Number(rowQteStk) : 0,
          name: row.entrepot_name || row.ENTREPOT_NAME || null,
          label: row.entrepot_name || row.ENTREPOT_NAME || rowCodeEnt
        }];
        console.log('[DEBUG] getWarehouseStocks: Returning direct stock:', result);
        return result;
      }
      console.log('[DEBUG] getWarehouseStocks: No direct stock found, returning empty array');
      return [];
    }

    const result = stocks
      .filter((stock) => stock)
      .map((stock, index) => {
        // Log ALL keys in the stock object to see what properties exist
        const stockKeys = Object.keys(stock || {});
        console.log(`[DEBUG] getWarehouseStocks: Stock ${index} object keys:`, stockKeys);
        console.log(`[DEBUG] getWarehouseStocks: Stock ${index} full object:`, stock);
        
        const quantity = Number(stock?.qte_stk ?? stock?.QTE_STK ?? stock?.quantity ?? stock?.QUANTITY ?? 0);
        const code = stock?.code_ent || 
                     stock?.CODE_ENT || 
                     stock?.code || 
                     stock?.code_entrepot || 
                     stock?.CODE_ENTREPOT ||
                     stock?.entrepot_code ||
                     stock?.ENTREPOT_CODE ||
                     'Entrepôt';
        const entrepotName = stock?.name || 
                            stock?.NAME || 
                            stock?.entrepot_name || 
                            stock?.ENTREPOT_NAME ||
                            stock?.entrepot?.name ||
                            stock?.entrepot?.NAME ||
                            null;
        
        console.log(`[DEBUG] getWarehouseStocks: Stock ${index} extracted values:`, {
          qte_stk: stock?.qte_stk,
          QTE_STK: stock?.QTE_STK,
          code_ent: stock?.code_ent,
          CODE_ENT: stock?.CODE_ENT,
          code: stock?.code,
          code_entrepot: stock?.code_entrepot,
          CODE_ENTREPOT: stock?.CODE_ENTREPOT,
          extractedCode: code,
          extractedQuantity: quantity
        });
        
        const processedStock = {
          ...stock,
          quantity: Number.isFinite(quantity) ? quantity : 0,
          code_ent: code,
          code,
          name: entrepotName,
          label: entrepotName || code
        };
        return processedStock;
      });
    
    console.log('[DEBUG] getWarehouseStocks: Final processed stocks array:', result);
    return result;
  }, []);

  const calculateTotalStock = useCallback((row, warehouseStocks) => {
    const totalFromWarehouses = warehouseStocks.reduce(
      (acc, stock) => acc + (Number(stock?.quantity) || 0),
      0
    );

    if (totalFromWarehouses > 0) {
      return totalFromWarehouses;
    }

    const fallback = Number(row?.article?.qte_stk ?? row?.qte_stk ?? 0);
    return Number.isFinite(fallback) ? fallback : 0;
  }, []);

  // Column definitions - adaptable based on type
  const getColumns = () => {
    const baseColumns = [
      {
        field: 'CODE_ART',
        headerName: type === 'inventaire' ? 'Code Article' : 'Code Art',
        width: 120,
        headerAlign: 'left',
        align: 'left',
        renderCell: (params) => {
          return <div style={{ width: '100%', textAlign: 'left' }}>{params.value || ''}</div>;
        }
      },
      {
        field: 'ARTICLE',
        headerName: type === 'inventaire' ? 'Désignation' : 'Article',
        flex: 1,
        headerAlign: 'left',
        align: 'left',
        renderCell: (params) => {
          return <div style={{ width: '100%', textAlign: 'left' }}>{params.value || ''}</div>;
        }
      },
      {
        field: 'info',
        headerName: '',
        width: 40,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const art = articleCache[params.row.CODE_ART];
          const isLoading = infoLoading && params.row === infoRow && !art;
          const pvHT = art?.PV_HT ?? params.row.PV_HT;
          const pvTTC = art?.PV_TTC ?? params.row.PV_TTC;
          const paHT = art?.PA_HT ?? params.row.PA_HT;
          const paTTC = art?.PA_TTC ?? params.row.PA_TTC;
          const warehouseStocks = getWarehouseStocks(params.row);
          const totalStock = calculateTotalStock(params.row, warehouseStocks);

          const popoverContent = isLoading ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={16} />
              <Typography variant="caption">Chargement...</Typography>
            </Stack>
          ) : (
            <Box>
              <Typography variant="subtitle2">{art?.ARTICLE || params.row.ARTICLE}</Typography>
              <Typography variant="caption" color="textSecondary">Code: {art?.code_art || params.row.CODE_ART}</Typography>
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2">PV HT: {pvHT !== undefined ? Number(pvHT).toFixed(3) : 'N/A'}</Typography>
                <Typography variant="body2">PV TTC: {pvTTC !== undefined ? Number(pvTTC).toFixed(3) : 'N/A'}</Typography>
                <Typography variant="body2">PA HT: {paHT !== undefined ? Number(paHT).toFixed(3) : 'N/A'}</Typography>
                <Typography variant="body2">PA TTC: {paTTC !== undefined ? Number(paTTC).toFixed(3) : 'N/A'}</Typography>
              </Box>
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="textSecondary">Stock total</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatStockValue(totalStock)}</Typography>
              </Box>
              {warehouseStocks.length > 0 ? (
                <Stack spacing={0.5} sx={{ mt: 1 }}>
                  <Typography variant="caption" color="textSecondary">Répartition par entrepôt</Typography>
                  {warehouseStocks.map((stock, idx) => {
                    const color = STOCK_COLOR_PALETTE[idx % STOCK_COLOR_PALETTE.length];
                    return (
                      <Stack
                        key={`${params.row.CODE_ART}-${stock.code}-${idx}`}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                      >
                        <Chip
                          size="small"
                          label={`${stock.code_ent || stock.code} ${stock.name || ''}`}
                          sx={{
                            height: 20,
                            fontSize: '0.7rem',
                            backgroundColor: alpha(color, 0.12),
                            color,
                            fontWeight: 600,
                            borderRadius: '999px'
                          }}
                        />
                        <Typography variant="caption" sx={{ fontWeight: 600, color }}>{formatStockValue(stock.quantity)}</Typography>
                        {stock.Etage !== null && stock.Etage !== undefined && (
                          <Typography variant="caption">Étage: {stock.Etage}</Typography>
                        )}
                        {stock.Zone && (
                          <Typography variant="caption">Zone: {stock.Zone}</Typography>
                        )}
                      </Stack>
                    );
                  })}
                </Stack>
              ) : (
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                  Pas de détail par entrepôt.
                </Typography>
              )}
            </Box>
          );

          // Check if this cell is focused via keyboard navigation
          const isInfoCellFocused = focusedRowId === params.row.id && focusedField === 'info';
          const isManuallyToggled = manualPopoverToggle[params.row.id];
          const shouldShowPopover = showInfoPopover || isInfoCellFocused || isManuallyToggled;

          return (
            <Popover
              title="Infos Article"
              content={popoverContent}
              trigger="hover"
              open={shouldShowPopover && infoRow?.id === params.row.id}
              onOpenChange={async (visible) => {
                setShowInfoPopover(visible);
                if (visible) {
                  setInfoRow(params.row);
                  await fetchArticleInfo(params.row?.CODE_ART);
                } else {
                  if (!isInfoCellFocused && !isManuallyToggled) {
                    setInfoRow(null);
                  }
                }
              }}
              overlayStyle={{ pointerEvents: 'none' }}
            >
              <span 
                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && isInfoCellFocused) {
                    e.preventDefault();
                    e.stopPropagation();
                    // Toggle popover manually with Enter key
                    setManualPopoverToggle(prev => ({
                      ...prev,
                      [params.row.id]: !prev[params.row.id]
                    }));
                    if (!isManuallyToggled) {
                      setInfoRow(params.row);
                      fetchArticleInfo(params.row?.CODE_ART);
                    }
                  }
                }}
                tabIndex={-1}
              >
                <InfoCircleOutlined style={{ fontSize: 14, color: '#1890ff' }} />
              </span>
            </Popover>
          );
        }
      },
    ];

    // Inventory keeps its original columns and does not display the article
    // information shortcut used by purchasing documents.
    if (type === 'inventaire') {
      baseColumns.pop();
    }

    // Quantity column
    baseColumns.push({
      field: 'QTE',
      headerName: type === 'inventaire' ? 'Qté Inv' : 'Quantité',
      width: 90,
      editable: true,
      cellClassName: 'editable-cell',
      headerAlign: 'right',
      align: 'right',
      valueParser: (value) => {
        const parsed = Number(String(value).replace(',', '.'));
        if (!Number.isFinite(parsed)) return 0;
        if (type === 'inventaire') return Math.trunc(parsed);
        return ['demarque', 'correctionstock'].includes(type) ? Math.abs(parsed) : parsed;
      },
      renderCell: (params) => {
        if (!params || !params.row || params.value === -1) return <div style={{ width: '100%', textAlign: 'right' }}>N/A</div>;
        const displayValue = type === 'correctionstock'
          ? Math.abs(Number(params.value) || 0) * quantitySign
          : params.value;
        return (
          <div style={{ width: '100%', textAlign: 'right' }}>
            {type === 'inventaire' ? formatAmount(displayValue, 0) : displayValue}
          </div>
        );
      }
    });

    if (type === 'inventaire') {
      baseColumns.push({
        field: 'QTE_STOCK_ACTUEL',
        headerName: 'Qté Stock',
        width: 100,
        headerAlign: 'right',
        align: 'right',
        renderCell: (params) => (
          <div style={{ width: '100%', textAlign: 'right' }}>
            {params.value === null || params.value === undefined ? '—' : formatAmount(params.value, 0)}
          </div>
        ),
      });
    }

    if (['demarque', 'correctionstock'].includes(type)) {
      baseColumns.push({
        field: 'REMARQUE',
        headerName: 'Remarque',
        minWidth: 220,
        flex: 1,
        editable: true,
        cellClassName: 'editable-cell',
        headerAlign: 'left',
        align: 'left',
        renderCell: (params) => (
          <div style={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {params.value || ''}
          </div>
        ),
      });
    }

    // Price columns (not used for stock movements)
    if (!['transfert', 'demarque', 'correctionstock'].includes(type)) {
      baseColumns.push(
        {
          field: 'PA_HT',
          headerName: 'PA HT',
          width: 90,
          editable: true,
          type: 'number',
          cellClassName: 'price-cell editable-cell',
          headerAlign: 'right',
          align: 'right',
          valueParser: (value) => {
            const parsedValue = parseFloat(value.toString().replace(',', '.'));
            return isNaN(parsedValue) ? 0 : parsedValue;
          },
          renderCell: (params) => {
            if (params.value === undefined || params.value === null) return <div style={{ width: '100%', textAlign: 'right' }}></div>;
            try {
              const numValue = Number(params.value);
              // Display with 2 decimal places, but keep actual value unrounded for calculations
              if (isNaN(numValue)) return <div style={{ width: '100%', textAlign: 'right' }}>0.00</div>;
              return <div style={{ width: '100%', textAlign: 'right' }}>{type === 'inventaire' ? formatAmount(numValue, 2) : numValue.toFixed(2)}</div>;
            } catch (error) {
              return <div style={{ width: '100%', textAlign: 'right' }}>0.00</div>;
            }
          }
        },
        {
          field: 'TVA',
          headerName: 'TVA%',
          width: 70,
          editable: type !== 'inventaire',
          type: 'number',
          cellClassName: type === 'inventaire' ? '' : 'editable-cell',
          headerAlign: 'right',
          align: 'right',
          renderCell: (params) => {
            if (params.value === undefined || params.value === null) return <div style={{ width: '100%', textAlign: 'right' }}></div>;
            return <div style={{ width: '100%', textAlign: 'right' }}>{params.value}%</div>;
          }
        },
        {
          field: 'PA_TTC',
          headerName: 'PA TTC',
          width: 90,
          editable: true,
          type: 'number',
          cellClassName: 'price-cell editable-cell',
          headerAlign: 'right',
          align: 'right',
          valueParser: (value) => {
            const parsedValue = parseFloat(value.toString().replace(',', '.'));
            return isNaN(parsedValue) ? 0 : parsedValue;
          },
          renderCell: (params) => {
            if (params.value === undefined || params.value === null) return <div style={{ width: '100%', textAlign: 'right' }}></div>;
            try {
              const numValue = Number(params.value);
              // Display with 2 decimal places, but keep actual value unrounded for calculations
              if (isNaN(numValue)) return <div style={{ width: '100%', textAlign: 'right' }}>0.00</div>;
              return <div style={{ width: '100%', textAlign: 'right' }}>{type === 'inventaire' ? formatAmount(numValue, 2) : numValue.toFixed(2)}</div>;
            } catch (error) {
              return <div style={{ width: '100%', textAlign: 'right' }}>0.00</div>;
            }
          }
        },
        ...(type === 'inventaire' ? [] : [{
          field: 'REMISE',
          headerName: '%R',
          width: 70,
          editable: true,
          type: 'number',
          cellClassName: 'price-cell editable-cell',
          headerAlign: 'right',
          align: 'right',
          valueParser: (value) => {
            const parsedValue = parseFloat(value.toString().replace(',', '.'));
            return isNaN(parsedValue) ? 0 : Math.min(100, Math.max(0, parsedValue));
          },
          renderCell: (params) => {
            if (params.value === undefined || params.value === null) return <div style={{ width: '100%', textAlign: 'right' }}></div>;
            try {
              const numValue = Number(params.value);
              return <div style={{ width: '100%', textAlign: 'right' }}>{isNaN(numValue) ? '0.00%' : `${numValue.toFixed(2)}%`}</div>;
            } catch (error) {
              return <div style={{ width: '100%', textAlign: 'right' }}>0.00%</div>;
            }
          }
        }])
      );
    }

    if (type === 'avoir') {
      baseColumns.push({
        field: 'MAJ_STK', headerName: 'Màj stock', type: 'boolean',
        width: 100, editable: !validated,
      });
    }

    // Total columns (not used for stock movements)
    if (!['transfert', 'demarque', 'correctionstock'].includes(type)) {
      baseColumns.push(
        {
          field: 'TotalHT',
          headerName: 'Total HT',
          width: 100,
          headerAlign: 'right',
          align: 'right',
          renderCell: (params) => {
            if (!params || !params.row) return <div style={{ width: '100%', textAlign: 'right' }}></div>;
            const row = params.row;
            
            // Use stored TotalHT if available, otherwise calculate
            let totalHT;
            if (row.TotalHT !== undefined && row.TotalHT !== null) {
              totalHT = Number(row.TotalHT);
            } else {
              const quantity = row.QTE;
              if (quantity === -1 || quantity === undefined || quantity === null) {
                return <div style={{ width: '100%', textAlign: 'right' }}>N/A</div>;
              }
              const paHT = row.PA_HT !== undefined && row.PA_HT !== null ? Number(row.PA_HT) : 0;
              totalHT = Number(quantity) * paHT;
            }
            
            // Display with 2 decimal places without changing the calculated value
            if (isNaN(totalHT)) return <div style={{ width: '100%', textAlign: 'right' }}>N/A</div>;
            return <div style={{ width: '100%', textAlign: 'right' }}>{type === 'inventaire' ? formatAmount(totalHT, 2) : totalHT.toFixed(2)}</div>;
          }
        },
        {
          field: 'TotalTTC',
          headerName: 'Total TTC',
          width: 100,
          headerAlign: 'right',
          align: 'right',
          renderCell: (params) => {
            if (!params || !params.row) return <div style={{ width: '100%', textAlign: 'right' }}></div>;
            const row = params.row;
            
            // Use stored TotalTTC if available, otherwise calculate
            let totalTTC;
            if (row.TotalTTC !== undefined && row.TotalTTC !== null) {
              totalTTC = Number(row.TotalTTC);
            } else {
              const quantity = row.QTE;
              if (quantity === -1 || quantity === undefined || quantity === null) {
                return <div style={{ width: '100%', textAlign: 'right' }}>N/A</div>;
              }
              const paTTC = row.PA_TTC !== undefined && row.PA_TTC !== null ? Number(row.PA_TTC) : 0;
              totalTTC = Number(quantity) * paTTC;
            }
            
            // Display with 2 decimal places without changing the calculated value
            if (isNaN(totalTTC)) return <div style={{ width: '100%', textAlign: 'right' }}>N/A</div>;
            return <div style={{ width: '100%', textAlign: 'right' }}>{type === 'inventaire' ? formatAmount(totalTTC, 2) : totalTTC.toFixed(2)}</div>;
          }
        }
      );
    }

    if (type === 'inventaire') {
      const inventoryColumnOrder = [
        'CODE_ART',
        'ARTICLE',
        'QTE',
        'QTE_STOCK_ACTUEL',
        'TVA',
        'PA_HT',
        'PA_TTC',
        'TotalHT',
        'TotalTTC',
      ];
      baseColumns.sort(
        (left, right) => inventoryColumnOrder.indexOf(left.field) - inventoryColumnOrder.indexOf(right.field)
      );
    }

    // Add stock column at the end (far right) if showStock is true and any row has stock data
    const hasStockData = processedRows.length > 0 && processedRows.some(row => 
      row.article?.stocks || 
      row.stocks || 
      row.qte_stk !== undefined || 
      row.QTE_STK !== undefined ||
      row.CODE_ENT || 
      row.code_ent ||
      row.article?.qte_stk !== undefined
    );
    
    console.log('[DEBUG] Stock column check:', {
      showStock,
      processedRowsLength: processedRows.length,
      hasStockData,
      codeEnt,
      firstRowSample: processedRows[0] ? {
        id: processedRows[0].id,
        CODE_ART: processedRows[0].CODE_ART,
        hasArticle: !!processedRows[0].article,
        articleStocks: processedRows[0].article?.stocks,
        stocks: processedRows[0].stocks,
        qte_stk: processedRows[0].qte_stk,
        QTE_STK: processedRows[0].QTE_STK,
        CODE_ENT: processedRows[0].CODE_ENT,
        code_ent: processedRows[0].code_ent
      } : null
    });
    
    if (showStock && hasStockData) {
      baseColumns.push({
        field: 'qte_stk',
        headerName: 'Stock',
        width: 70,
        sortable: true,
        headerAlign: 'right',
        align: 'right',
        valueGetter: (params) => {
          console.log('[DEBUG] Stock valueGetter called:', {
            hasParams: !!params,
            hasRow: !!params?.row,
            rowId: params?.row?.id,
            CODE_ART: params?.row?.CODE_ART,
            codeEnt,
            codeEntType: typeof codeEnt
          });
          
          if (!params || !params.row || !codeEnt) {
            console.log('[DEBUG] Stock valueGetter: Missing params/row/codeEnt, returning 0');
            return 0;
          }
          
          const warehouseStocks = getWarehouseStocks(params.row);
          console.log('[DEBUG] Stock valueGetter: warehouseStocks:', warehouseStocks);
          
          // Case-insensitive comparison and trim for matching
          const normalizedCodeEnt = String(codeEnt).trim().toLowerCase();
          console.log('[DEBUG] Stock valueGetter: Looking for codeEnt:', normalizedCodeEnt);
          
          const selectedEntStock = warehouseStocks.find(s => {
            // Check all possible code property variations
            const stockCode = String(
              s.code_ent || 
              s.CODE_ENT || 
              s.code || 
              s.code_entrepot ||
              s.CODE_ENTREPOT ||
              s.entrepot_code ||
              s.ENTREPOT_CODE ||
              ''
            ).trim().toLowerCase();
            const matches = stockCode === normalizedCodeEnt;
            console.log('[DEBUG] Stock valueGetter: Comparing:', {
              stockCode,
              normalizedCodeEnt,
              matches,
              stockObject: s
            });
            return matches;
          });
          
          const result = selectedEntStock ? selectedEntStock.quantity : 0;
          console.log('[DEBUG] Stock valueGetter: Result:', {
            selectedEntStock,
            quantity: result
          });
          
          return result;
        },
        sortComparator: (v1, v2) => (Number(v1) || 0) - (Number(v2) || 0),
        renderCell: (params) => {
          console.log('[DEBUG] Stock renderCell called:', {
            hasParams: !!params,
            hasRow: !!params?.row,
            rowId: params?.row?.id,
            CODE_ART: params?.row?.CODE_ART,
            codeEnt,
            value: params?.value
          });
          
          if (!params || !params.row) {
            console.log('[DEBUG] Stock renderCell: Missing params/row, returning 0');
            return <div style={{ width: '100%', textAlign: 'right' }}>{formatStockValue(0)}</div>;
          }

          // If no entrepot is selected, show message
          if (!codeEnt) {
            console.log('[DEBUG] Stock renderCell: No codeEnt selected');
            return (
              <div style={{ width: '100%', textAlign: 'right' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                  Select Entrepôt
                </Typography>
              </div>
            );
          }

          // Get stock only for the selected entrepot - case-insensitive comparison
          const warehouseStocks = getWarehouseStocks(params.row);
          console.log('[DEBUG] Stock renderCell: warehouseStocks (full):', JSON.stringify(warehouseStocks, null, 2));
          console.log('[DEBUG] Stock renderCell: All stock codes available:', warehouseStocks.map(s => ({
            code_ent: s.code_ent,
            CODE_ENT: s.CODE_ENT,
            code: s.code,
            normalized: String(s.code_ent || s.CODE_ENT || s.code || '').trim().toLowerCase()
          })));
          
          const normalizedCodeEnt = String(codeEnt).trim().toLowerCase();
          console.log('[DEBUG] Stock renderCell: Looking for codeEnt:', {
            original: codeEnt,
            normalized: normalizedCodeEnt,
            type: typeof codeEnt
          });
          
          const selectedEntStock = warehouseStocks.find(s => {
            // Check all possible code property variations
            const stockCode = String(
              s.code_ent || 
              s.CODE_ENT || 
              s.code || 
              s.code_entrepot ||
              s.CODE_ENTREPOT ||
              s.entrepot_code ||
              s.ENTREPOT_CODE ||
              ''
            ).trim().toLowerCase();
            const matches = stockCode === normalizedCodeEnt;
            console.log('[DEBUG] Stock renderCell: Comparing stock:', {
              stockObject: s,
              stockCode,
              normalizedCodeEnt,
              matches,
              allCodeVariations: {
                code_ent: s.code_ent,
                CODE_ENT: s.CODE_ENT,
                code: s.code,
                code_entrepot: s.code_entrepot,
                CODE_ENTREPOT: s.CODE_ENTREPOT
              }
            });
            return matches;
          });
          
          const stockQty = selectedEntStock ? selectedEntStock.quantity : 0;
          const isOutOfStock = stockQty <= 0;
          
          console.log('[DEBUG] Stock renderCell: Final result:', {
            selectedEntStock,
            stockQty,
            isOutOfStock,
            formattedValue: formatStockValue(stockQty)
          });

          return (
            <div style={{ width: '100%', textAlign: 'right' }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: isOutOfStock ? 'error.main' : 'text.primary',
                }}
              >
                {formatStockValue(stockQty)}
              </Typography>
            </div>
          );
        }
      });
    }

    // Actions column
    baseColumns.push({
      field: 'actions',
      headerName: type === 'inventaire' ? 'Actions' : '',
      width: 70,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        if (!params || !params.row) return null;
        
        const canDelete = currentCan(null, 'update') && (!validated || userPermissions?.isManager);
        
        return canDelete ? (
          <div 
            onClick={() => setConfirmDelete(params.row.id)}
            style={{ 
              cursor: 'pointer', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              width: '100%',
              height: '100%'
            }}
          >
            <span style={{ 
              fontSize: '14px', 
              letterSpacing: '2px',
              fontWeight: 'bold'
            }}>...</span>
          </div>
        ) : null;
      }
    });

    return baseColumns;
  };

  return (
    <Box sx={{
      width: '100%',
      '& .highlighted-cell': {
        color: '#ff8c00',
      },
      '& .editable-cell': {
        backgroundColor: 'rgba(25, 118, 210, 0.12)',
      },
      '& .price-cell': {
        color: '#1976d2',
      },
      '& .stock-overflow-cell': {
        overflow: 'visible !important',
        position: 'relative',
        zIndex: 1,
      },
      '& .focused-row': {
        borderTop: '2px solid black',
        borderBottom: '2px solid black',
      }
    }}>
      
      <DataGrid
        ref={gridRef}
        apiRef={apiRef}
        rows={processedRows}
        columns={getColumns()}
        getRowId={(row) => row.id}
        rowHeight={25}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(error) => console.error('Error updating row:', error)}
        isCellEditable={isCellEditable}
        onCellKeyDown={handleCellKeyDown}
        disableColumnMenu
        
        // Selection configuration - only show checkboxes when selectionMode is enabled
        checkboxSelection={(type === 'commande' || type === 'reception') && selectionMode}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={(newSelection) => {
          setRowSelectionModel(newSelection);
          if (onSelectionChange) {
            const selectedItems = processedRows.filter(row => newSelection.includes(row.id));
            onSelectionChange(selectedItems);
          }
        }}
        
        // Keep cell editing enabled even with checkboxes
        disableRowSelectionOnClick={true}
        
        onCellValueChange={handleCellValueChange}
        onCellFocusOut={(params) => {
          const { id, field } = params;
          const tempValue = tempInputValues[`${id}-${field}`];
          if (tempValue !== undefined) {
            const row = processedRows.find(r => r.id === id);
            if (row) {
              const updatedRow = { ...row, [field]: tempValue };
              processRowUpdate(updatedRow, row);
            }
          }
        }}
        
        onCellClick={(params) => {
          setFocusedRowId(params.id);
        }}
        
        onRowClick={(params) => {
          setFocusedRowId(params.id);
        }}
        
        onCellFocusIn={async (params) => {
          setFocusedRowId(params.id);
          setFocusedField(params.field);
          
          // If focusing on info cell, show popover and fetch data
          if (params.field === 'info') {
            const row = processedRows.find(r => r.id === params.id);
            if (row) {
              setInfoRow(row);
              await fetchArticleInfo(row.CODE_ART);
            }
          } else {
            // Hide popover and clear manual toggle when navigating away from info cell
            if (focusedField === 'info') {
              setInfoRow(null);
              // Clear manual toggle for the previous row
              if (focusedRowId) {
                setManualPopoverToggle(prev => ({
                  ...prev,
                  [focusedRowId]: false
                }));
              }
            }
          }
        }}
        
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[PAGE_SIZE]}
        slots={{
          pagination: CustomPagination,
        }}
        
        autoHeight={false}
        sx={{
          height: 800,
          maxHeight: '65vh',
          overflowY: 'auto',
          // Cell base styling
          [`& .${gridClasses.cell}`]: {
            fontSize: '0.8rem',
            padding: '1px 8px',
          },
          // Force text alignment for cells - MUI DataGrid alignment classes
          '& .MuiDataGrid-cell--textLeft': {
            justifyContent: 'flex-start',
            textAlign: 'left',
          },
          '& .MuiDataGrid-cell--textRight': {
            justifyContent: 'flex-end',
            textAlign: 'right',
          },
          '& .MuiDataGrid-cell--textCenter': {
            justifyContent: 'center',
            textAlign: 'center',
          },
          // Header alignment
          '& .MuiDataGrid-columnHeader--alignLeft .MuiDataGrid-columnHeaderTitle': {
            textAlign: 'left',
          },
          '& .MuiDataGrid-columnHeader--alignRight .MuiDataGrid-columnHeaderTitle': {
            textAlign: 'right',
          },
          '& .MuiDataGrid-columnHeader--alignCenter .MuiDataGrid-columnHeaderTitle': {
            textAlign: 'center',
          },
          // Also force alignment on the columnHeader container
          '& .MuiDataGrid-columnHeader--alignRight': {
            '& .MuiDataGrid-columnHeaderTitleContainer': {
              justifyContent: 'flex-end',
            },
          },
          '& .MuiDataGrid-columnHeader--alignLeft': {
            '& .MuiDataGrid-columnHeaderTitleContainer': {
              justifyContent: 'flex-start',
            },
          },
          [`& .${gridClasses.cell}:focus`]: {
            outline: 'none',
            border: 'solid #000000 1px',
            borderRadius: '4px',
          },
          [`& .${gridClasses.row}:hover`]: {
            backgroundColor: 'rgba(115, 127, 139, 0.08)',
            cursor: 'pointer',
          },
          [`& .${gridClasses.row}.Mui-selected`]: {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            '&:hover': {
              backgroundColor: 'rgba(115, 127, 139, 0.12)',
            },
          },
          '& .MuiDataGrid-footerContainer': {
            border: 'none',
          }
        }}
        
        initialState={{
          sorting: {
            sortModel: ['commande', 'reception'].includes(type)
              ? []
              : [{ field: 'CODE_ART', sort: 'asc' }],
          },
        }}
        
        getRowClassName={(params) => {
          const classes = [];
          
          if (params.id === focusedRowId) {
            classes.push('focused-row');
          }
          
          return classes.join(' ');
        }}
        
        componentsProps={{
          cell: {
            onKeyDown: (e) => {
              if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'].includes(e.key)) {
                return;
              }
              if (!e.ctrlKey && !e.metaKey && !e.altKey && e.key.length === 1) {
                e.defaultMuiPrevented = true;
              }
            }
          }
        }}
        
        editMode="cell"
        disableMultipleColumnsSorting
      />

      {/* Delete confirmation dialog */}
      <Dialog
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
      >
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Êtes-vous sûr de vouloir supprimer cet article?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <PermissionButton  permissionLibrary="mui" onClick={() => setConfirmDelete(null)}>
            Non
          </PermissionButton>
          <PermissionButton permissionAction="update" permissionLibrary="mui"
            onClick={() => {
              const row = processedRows.find(r => r.id === confirmDelete);
              handleDelete(['commande', 'reception', 'avoir', 'facture'].includes(type)
                ? confirmDelete : (row?.CODE_ART ?? confirmDelete));
              setConfirmDelete(null);
            }} 
            color="error" 
            autoFocus
          >
            Oui
          </PermissionButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NestedAchatTable;
